import { Permission, permissionSchema, UserColumns, userEntityName } from '@/backend/features/authentication/shared';
import { useTranslation } from '@/ui/hooks';
import { Column, TextCell } from '@juvare/common-data-table-ui/DataTable';
import { useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { useEntityDataTable } from '@juvare/platform-sdk-react/ui/hooks';
import { Row } from '@juvare/platform-shared/features/storage';
import React from 'react';
import { PermissionIcons } from '../components/PermissionIcons';
import { UserActions } from '../components/UserActions';
import { UserFilterData } from '../types/UserFilterData';
import { mapUserFilterData } from '../utils/mapUserFilterData';

export function useUserTable() {
    const { t } = useTranslation();
    const filterConfig = useFilterConfig<UserFilterData>((builder) => {
        builder.quickSearchFilter({
            label: t('search'),
            dataPath: 'search',
            sx: { maxWidth: 320 },
        });
        builder.quickFilterMultiSelect({
            label: t('permissions'),
            dataPath: 'permissions',
            sx: { maxWidth: 380 },
            values: permissionSchema.options.map((permission) => ({
                value: permission,
                label: t(`permissionEnum.${permission}`),
            })),
            widget: 'form.checkboxList',
        });
    }, []);
    const columns = React.useMemo<Column<Row<UserColumns>>[]>(
        () => [
            {
                headerLabel: t('email'),
                cellFunction: (row) => <TextCell>{row.columns.email}</TextCell>,
            },
            {
                headerLabel: t('firstName'),
                cellFunction: (row) => <TextCell>{row.columns.firstName}</TextCell>,
            },
            {
                headerLabel: t('lastName'),
                cellFunction: (row) => <TextCell>{row.columns.lastName}</TextCell>,
            },
            {
                headerLabel: t('permissions'),
                cellFunction: (row) => (
                    <TextCell sx={{ color: themeColors.text.subtle }}>
                        <PermissionIcons permissions={row.columns.permissions as Permission[]} />
                    </TextCell>
                ),
            },
            {
                headerLabel: t('actions'),
                cellComponent: UserActions,
                tableCellSx: { width: 0 },
            },
        ],
        [t],
    );
    return useEntityDataTable<UserFilterData, UserColumns>(
        {
            entityName: userEntityName,
            title: t('users'),
            filterConfig: filterConfig,
            columns: columns,
            mapEntityFilterData: mapUserFilterData,
            defaultSortColumnKey: 'email',
        },
        {},
    );
}
